import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientListQuery } from 'api';
import { Form, TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { ClientDataType } from 'pages/admin/clients/index';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { ClientListArgument } from 'typings/client';

export type IFilterParams = {
  dealCountMax?: number;
  dealCountMin?: number;
  dealAmountMin?: number;
  dealAmountMax?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  lastUpdatedDateFrom?: string;
  lastUpdatedDateTo?: string;
  salesManagerId?: string;
  purchasingManagerId?: string;
  countryId?: string;
  clientTypeId?: string;
  priorityLevel?: string;
  verifiedStatus?: boolean | string;
  createdDate?: any;
  lastUpdatedDate?: any;
  lastActivityDate?: any;
  tags: string[];
};

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useClients = () => {
  const [modalState, setModalState] = useState<boolean>(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<IFilterParams | object>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sorter, setSorter] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string>('');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { data: { data: { content = [], totalElements = 0 } = {} } = {}, isLoading } = useClientListQuery({
    size: pagination.size,
    page: pagination.page,
    sort: sorter.field && sorter.order ? `${sorter.field},${sorter.order}` : undefined,
    search,
    ...filters,
  } as ClientListArgument);
  const { clients = [] } = content?.[0] || {};

  const openDrawer = useCallback(() => setFilterDrawerVisible(true), []);
  const closeDrawer = useCallback(() => setFilterDrawerVisible(false), []);
  const openModal = useCallback(() => setModalState(true), []);
  const closeModal = useCallback(() => setModalState(false), []);

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: IFilterParams) => {
      const _filters: any = {
        ...(values.dealAmountMin && { dealAmountMin: values.dealAmountMin }),
        ...(values.dealAmountMax && { dealAmountMax: values.dealAmountMax }),
        ...(values.dealCountMin && { dealCountMin: values.dealCountMin }),
        ...(values.dealCountMax && { dealCountMax: values.dealCountMax }),
        ...(values.priorityLevel && { priorityLevel: values.priorityLevel }),
        ...(values.salesManagerId && { salesManagerId: values.salesManagerId }),
        ...(values.purchasingManagerId && { purchasingManagerId: values.purchasingManagerId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.clientTypeId && { clientTypeId: values.clientTypeId }),
        ...(values.verifiedStatus !== '' &&
          values.verifiedStatus !== null && { verifiedStatus: values.verifiedStatus }),
        ...(values.tags?.length && { tags: values.tags }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
        ...(values.lastActivityDate?.[0] && {
          lastActivityDateFrom: dateFormatter(values.lastActivityDate[0], 'start'),
        }),
        ...(values.lastActivityDate?.[1] && {
          lastActivityDateTo: dateFormatter(values.lastActivityDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      closeDrawer();
    },
    [closeDrawer]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  const routeChange = useCallback(
    (id: string) => {
      navigate(`${ADMIN_ROUTES.client}/${id}/general`);
    },
    [navigate]
  );

  const handleTableChange: TableProps<ClientDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ClientDataType> | SorterResult<ClientDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSorter({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );
  return {
    openDrawer,
    closeDrawer,
    modalState,
    openModal,
    closeModal,
    filterDrawerVisible,
    form,
    routeChange,
    data: clients || [],
    totalElements: totalElements || 0,
    pagination,
    isLoading,
    onSearch,
    handleTableChange,
    applyFilters,
    resetFilters,
  };
};
